/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [navigation.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 27.09.18 17:46
 */

import React, {Fragment, useEffect, useState} from 'react';
import NavigationUser from "./navigation-user";
import {Link, useLocation} from "react-router-dom";
import {useDispatch} from 'react-redux';
import {asyncConnectAuth} from "../../actions";

import {webConnect} from '../messaging/web-connect';
import {useAuthorization, useSiteConfiguration} from "../../selectors";
import {getImprintPageUrl, getUserAvatar} from "../../actions/environment";
import SecondaryNavigation from "./secondary-navigation";
import NavigationSearchDropdown from "./navigation-search-dropdown";
import NavigationSlideoutToggler from "./navigation-slideout-toggler";
import NavigationUserActionsDropdown from "./navigation-user-actions-dropdown";
import {useConfiguration, useIsMobile} from "../configuration/configuration";
import NavigationLegalDropdown from "./navigation-legal-dropdown";
import {TooltipLink} from "./buttons/nav-buttons";
import {Popover} from "./popover/popover";
import NavigationLanguageDropdown, {localeFlags} from "./navigation-language-dropdown";
import {
    AccountCircleIcon,
    AppsIcon,
    CelebrationIcon,
    fs_bigger, fs_regular,
    HomeIcon2,
    SearchIcon,
    TodayIcon
} from "./icons/nav-icons";

const Navigation = () => {
    const [showSearch, setShowSearch] = useState(false);
    const {authorization, isAuthorized, principalUsername, principal} = useAuthorization();
    const {localConfiguration, homePage, eventsCalendarPage, spacesCalendarPage, festivalsCalendarPage} = useSiteConfiguration();
    const {Lang, Copy} = useConfiguration();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const location = useLocation();

    useEffect(() => {
        if (authorization && authorization.status === 'connect') {
            dispatch(asyncConnectAuth(principalUsername));
        } else {
            isAuthorized && webConnect(dispatch)(isAuthorized, principalUsername, location);
        }
    }, [authorization])

    const renderCurrentUser = () => {
        if (isAuthorized) {
            const name = principal.firstname;
            const avatar = getUserAvatar(principal);
            return <NavigationUser avatar={avatar} name={name} to={`/${principalUsername}/home`}/>;
        }
        return <NavigationUser to='/login' name={Lang.nav.notLoggedIn}/>;
    }

    const renderPageLink = (authorization, page, label) => {
        return <Link className='nav-link' to={`/${principalUsername}/page/${page}`}>
            {label}
        </Link>
    }

    const handleShowSearch = (event) => {
        event.preventDefault();
        setShowSearch(!showSearch);
    }

    return (
        <div className='row navigation fixed-header' id='main-navigation'>
            <nav className="navbar navbar-expand-md navbar-dark navbar-bg-color">
                <Link className="navigation-main-brand" to={'/'}>
                    {Copy.navigation.logo && <div className="navigation-main-logo navigation-rotate-logo mr-2">
                        <img src={Copy.navigation.logo}/>
                    </div>}
                    {Copy && <div className="d-inline">
                        <div className="logo-text-principal">{Copy.navigation.fullName}</div>
                        <div className="logo-text-secondary">{Copy.navigation.shortName}</div>
                    </div>}
                </Link>
                {isMobile && <NavigationSlideoutToggler/>}

                {/* LEFT SIDE */}
                <div className="collapse navbar-collapse offcanvas-collapse" id="navbarTogglerId">
                    <ul className="navbar-nav me-auto">
                        <li className="nav-item">
                            <TooltipLink className='nav-link box-aligned' to={homePage}>
                                <HomeIcon2 size={fs_bigger} className='mr-1'/>
                                <span className='nav-item-hide'>{Lang.nav.header.home}</span>
                            </TooltipLink>
                        </li>
                        <li className="nav-item">
                            <TooltipLink className='nav-link box-aligned' to={eventsCalendarPage}>
                                <TodayIcon size={fs_bigger} className='mr-1'/>
                                <span className='nav-item-hide'>{Lang.nav.header.events.text}</span>
                            </TooltipLink>
                        </li>
                        <li className="nav-item">
                            <TooltipLink className='nav-link box-aligned' to={festivalsCalendarPage}>
                                <CelebrationIcon size={fs_bigger} className='mr-1'/>
                                <span className='nav-item-hide'>{Lang.nav.header.festivals.text}</span>
                            </TooltipLink>
                        </li>
                        <li className="nav-item">
                            <TooltipLink className='nav-link box-aligned' to={spacesCalendarPage}>
                                <AppsIcon size={fs_bigger} className='mr-1'/>
                                <span className='nav-item-hide'>{Lang.nav.header.spaces.text}</span>
                            </TooltipLink>
                        </li>
                    </ul>

                    <ul className="navbar-nav ms-auto mr-5">
                        <div className="btn-group mr-3" role="group">
                            {renderPageLink(authorization, getImprintPageUrl(), Lang.nav.header.imprint)}
                            <button type="button" className="dropdown-toggle btn btn-sm"
                                    data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            </button>
                            <NavigationLegalDropdown principalUsername={principalUsername}/>
                        </div>

                        <div className="btn-group mr-3" role="group">
                            <button type="button" className="dropdown-toggle btn btn-sm"
                                    data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                {localeFlags[Lang.locale]}
                            </button>
                            <NavigationLanguageDropdown/>
                        </div>
                    </ul>

                    {/* RIGHT SIDE */}
                    <div className="btn-group mr-3" role="group">
                        {authorization && renderCurrentUser()}
                        <button type="button" className="dropdown-toggle btn btn-sm"
                                data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <AccountCircleIcon size={fs_bigger}/>
                        </button>
                        <NavigationUserActionsDropdown/>
                    </div>

                    {isAuthorized && <div className="nav-item">
                        <Popover className='popover-search-container'
                                 render={({close, labelId, descriptionId}) => {
                                     return <Fragment>
                                         <NavigationSearchDropdown onClose={close}/>
                                     </Fragment>
                                 }}>
                            <button className="nav-link btn btn-sm"><SearchIcon size={fs_regular}/></button>
                        </Popover>
                    </div>}
                </div>
            </nav>
            <SecondaryNavigation Copy={Copy}/>
        </div>
    );
}

export default Navigation;